import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Plus, Search, Trash2, Send, Download, Mail, Bell, Loader2, Link2, FileCheck, RotateCcw, CheckCircle2, Pencil, FileSpreadsheet } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { useAdminLanguage } from "../contexts/AdminLanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["all", "draft", "sent", "paid", "overdue", "credited", "cancelled"];
const TYPES = ["all", "one_time", "recurring", "credit_note"];
const STATUS_COLORS = {
  draft: "bg-neutral-500/10 text-[#857973] border-neutral-500/30",
  sent: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  paid: "bg-green-500/10 text-green-400 border-green-500/30",
  overdue: "bg-red-500/10 text-red-400 border-red-500/30",
  credited: "bg-orange-500/10 text-orange-400 border-orange-500/30",
  cancelled: "bg-neutral-600/10 text-[#857973] border-neutral-600/30",
};

export default function InvoicesPage() {
  const { t } = useAdminLanguage();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [sendDialog, setSendDialog] = useState(null);
  const [sendEmail, setSendEmail] = useState("");
  const [sendType, setSendType] = useState("invoice");
  const [sendPdf, setSendPdf] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [items, setItems] = useState([{ description: "", quantity: 1, unit_price: 0, article_id: "" }]);
  const defaultDueDate = () => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10); };
  const [form, setForm] = useState({ customer_id: "", type: "one_time", status: "draft", due_date: defaultDueDate(), due_days: 30, recurring_interval: "monthly", currency: "EUR", vat_option: "auto" });
  const [discount, setDiscount] = useState({ type: "percentage", value: 0, description: "" });
  const [articles, setArticles] = useState([]);
  const [formErrors, setFormErrors] = useState([]);

  const fetch = useCallback(async () => {
    const [invRes, custRes, artRes] = await Promise.all([
      axios.get(`${API}/admin/invoices`, { params: { status: filterStatus, type: filterType }, withCredentials: true }),
      axios.get(`${API}/admin/customers-list`, { withCredentials: true }),
      axios.get(`${API}/admin/articles`, { params: { active: "true" }, withCredentials: true }),
    ]);
    setInvoices(invRes.data);
    setCustomers(custRes.data);
    setArticles(artRes.data);
  }, [filterStatus, filterType]);

  useEffect(() => { fetch(); }, [fetch]);

  const custName = (id) => customers.find(c => c.id === id)?.company_name || "—";
  const subtotal = items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
  const discountAmount = discount.type === "percentage" ? subtotal * discount.value / 100 : discount.value;
  const total = Math.max(subtotal - (discount.value > 0 ? discountAmount : 0), 0);

  // Client-side search across id, customer name, and amount
  const filteredInvoices = useMemo(() => {
    if (!search.trim()) return invoices;
    const q = search.trim().toLowerCase();
    return invoices.filter(inv => {
      const shortId = (inv.id || "").slice(0, 8).toLowerCase();
      const cust = custName(inv.customer_id).toLowerCase();
      const amt = String(inv.total_amount || "");
      return shortId.includes(q) || cust.includes(q) || amt.includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices, search, customers]);

  const addArticleToItems = (articleId) => {
    const art = articles.find(a => a.id === articleId);
    if (!art) return;
    // Block recurring articles on one-time invoices — they must go through an Agreement
    // Articles model uses .type = "one_time" | "recurring"
    if (art.type === "recurring" && form.type !== "recurring") {
      const cadence = art.billing_interval === "yearly" ? "årligt" : art.billing_interval === "quarterly" ? "kvartalsvis" : "månatligt";
      alert(
        `"${art.name}" är en ${cadence} återkommande artikel och kan inte läggas på en engångsfaktura.\n\n` +
        `Skapa ett Avtal (Avtal-fliken) för hosting/support — där genereras fakturor automatiskt varje ${cadence === "årligt" ? "år" : "månad"}.`
      );
      return;
    }
    setItems(prev => [...prev, { description: art.name, quantity: 1, unit_price: art.unit_price, article_id: art.id }]);
  };

  const validateForm = () => {
    const errs = [];
    const isCredit = form.type === "credit_note";
    if (!form.customer_id) errs.push(t("inv_err_customer_required"));
    if (items.length === 0) errs.push(t("inv_err_no_items"));
    items.forEach((it, idx) => {
      const n = idx + 1;
      if (!it.description || !it.description.trim()) errs.push(t("inv_err_item_description", { n }));
      if (!(Number(it.quantity) > 0)) errs.push(t("inv_err_item_qty", { n }));
      // For normal (non-credit) invoices we require > 0. Credit notes allow negative prices/refund.
      if (!isCredit && !(Number(it.unit_price) > 0)) errs.push(t("inv_err_item_price", { n }));
    });
    if (!isCredit && total <= 0) errs.push(t("inv_err_zero_total"));
    return errs;
  };

  const createInvoice = async (e) => {
    e.preventDefault();
    const errs = validateForm();
    setFormErrors(errs);
    if (errs.length > 0) return;
    const payload = { ...form, items };
    if (discount.value > 0) payload.discount = discount;
    if (form.type !== "recurring") {
      delete payload.recurring_interval;
      delete payload.next_invoice_date;
    }
    if (editingId) {
      const updatePayload = {
        items, due_date: form.due_date,
        ...(discount.value > 0 ? { discount } : {}),
        ...(form.type === "recurring" ? { recurring_interval: form.recurring_interval } : {}),
      };
      try {
        await axios.put(`${API}/admin/invoices/${editingId}`, updatePayload, { withCredentials: true });
      } catch (err) {
        setFormErrors([err.response?.data?.detail || "Kunde inte spara"]);
        return;
      }
    } else {
      try {
        await axios.post(`${API}/admin/invoices`, payload, { withCredentials: true });
      } catch (err) {
        setFormErrors([err.response?.data?.detail || "Kunde inte skapa faktura"]);
        return;
      }
    }
    closeDialog();
    fetch();
  };

  const closeDialog = () => {
    setShowCreate(false);
    setEditingId(null);
    setItems([{ description: "", quantity: 1, unit_price: 0, article_id: "" }]);
    setForm({ customer_id: "", type: "one_time", status: "draft", due_date: defaultDueDate(), due_days: 30, recurring_interval: "monthly", currency: "EUR", vat_option: "auto" });
    setDiscount({ type: "percentage", value: 0, description: "" });
    setFormErrors([]);
  };

  const openEdit = (inv) => {
    setEditingId(inv.id);
    setItems(inv.items?.length ? inv.items.map(i => ({ ...i })) : [{ description: "", quantity: 1, unit_price: 0, article_id: "" }]);
    setForm({
      customer_id: inv.customer_id || "",
      type: inv.type || "one_time",
      status: inv.status || "draft",
      due_date: inv.due_date || defaultDueDate(),
      due_days: 30,
      recurring_interval: inv.recurring_interval || "monthly",
      currency: inv.currency || "EUR",
      vat_option: inv.vat_option || "auto",
    });
    setDiscount(inv.discount || { type: "percentage", value: 0, description: "" });
    setFormErrors([]);
    setShowCreate(true);
  };

  const updateStatus = async (id, status) => {
    const updates = { status };
    if (status === "paid") updates.paid_date = new Date().toISOString().slice(0, 10);
    await axios.put(`${API}/admin/invoices/${id}`, updates, { withCredentials: true });
    fetch();
  };

  const markAsPaid = async (inv) => {
    const shortId = inv.id.slice(0, 8).toUpperCase();
    const sendReceipt = window.confirm(t("inv_confirm_mark_paid", { id: shortId }));
    await updateStatus(inv.id, "paid");
    if (sendReceipt) {
      try {
        const recipient = inv.customer_email || (await axios.get(`${API}/admin/customers/${inv.customer_id}`, { withCredentials: true })).data?.email;
        if (recipient) {
          await axios.post(`${API}/admin/invoices/${inv.id}/send-receipt`, { recipient_email: recipient, include_pdf: true }, { withCredentials: true });
        }
      } catch (err) { /* silently ignore */ }
    }
  };

  const deleteInvoice = async (inv) => {
    const shortId = inv.id.slice(0, 8).toUpperCase();
    if (!window.confirm(t("inv_confirm_delete", { id: shortId }))) return;
    await axios.delete(`${API}/admin/invoices/${inv.id}`, { withCredentials: true });
    fetch();
  };

  const downloadPdf = async (id) => {
    try {
      const res = await axios.get(`${API}/admin/invoices/${id}/pdf`, { withCredentials: true, responseType: "blob", params: { origin_url: window.location.origin } });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `invoice-${id.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      alert("Kunde inte ladda ner faktura: " + (err.response?.statusText || err.message));
    }
  };

  const downloadReceipt = async (id) => {
    try {
      const res = await axios.get(`${API}/admin/invoices/${id}/receipt-pdf`, { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `receipt-${id.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      alert("Kunde inte ladda ner kvitto: " + (err.response?.statusText || err.message));
    }
  };

  const copyPayLink = (id) => {
    const link = `${window.location.origin}/pay/${id}`;
    navigator.clipboard.writeText(link);
    alert(t("inv_link_copied"));
  };

  const exportCsv = () => {
    const rows = [
      ["Fakturanummer", "Kund", "Typ", "Belopp", "Valuta", "Status", "Förfaller", "Skapad"],
      ...filteredInvoices.map(inv => [
        inv.id.slice(0, 8).toUpperCase(),
        custName(inv.customer_id),
        t(`inv_type_${inv.type}`) || inv.type,
        (inv.total_amount ?? 0).toFixed(2),
        inv.currency || "",
        t(`inv_status_${inv.status}`) || inv.status,
        inv.due_date || "",
        (inv.created_at || "").slice(0, 10),
      ]),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    // Add BOM for Excel UTF-8
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fakturor-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  };

  const openSendDialog = (inv, type = "invoice") => {
    const cust = customers.find(c => c.id === inv.customer_id);
    setSendDialog(inv);
    setSendEmail(cust?.billing_email || cust?.email || "");
    setSendType(type);
    setSendPdf(true);
    setSendResult(null);
  };

  const creditInvoice = async (inv) => {
    const shortId = inv.id.slice(0, 8).toUpperCase();
    const amount = `${inv.total_amount} ${inv.currency}`;
    if (!window.confirm(t("inv_confirm_credit", { id: shortId, amount }))) return;
    try {
      const { data } = await axios.post(`${API}/admin/invoices/${inv.id}/credit`, {}, { withCredentials: true });
      alert(`Kreditfaktura skapad: ${data.total} ${data.currency}`);
      fetch();
    } catch (err) { alert(err.response?.data?.detail || "Kunde inte kreditera"); }
  };

  const handleSend = async () => {
    if (!sendEmail || !sendDialog) return;
    setSending(true);
    setSendResult(null);
    try {
      const endpoint = sendType === "reminder" ? "send-reminder" : sendType === "receipt" ? "send-receipt" : "send";
      const { data } = await axios.post(`${API}/admin/invoices/${sendDialog.id}/${endpoint}`, {
        recipient_email: sendEmail, include_pdf: sendPdf, origin_url: window.location.origin
      }, { withCredentials: true });
      setSendResult({ success: true, message: data.message });
      fetch();
    } catch (err) {
      setSendResult({ success: false, message: err.response?.data?.detail || t("inv_send_failed") });
    } finally {
      setSending(false);
    }
  };

  const sendDialogTitle = sendType === "reminder" ? t("inv_send_reminder_title")
    : sendType === "receipt" ? t("inv_send_receipt_title") : t("inv_send_title");
  const sendBtnLabel = sendType === "reminder" ? t("inv_send_btn_reminder")
    : sendType === "receipt" ? t("inv_send_btn_receipt") : t("inv_send_btn_invoice");

  return (
    <div data-testid="invoices-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">{t("inv_title")}</h1>
          <p className="text-sm text-[#857973]">{t("inv_count", { n: filteredInvoices.length })}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={exportCsv}
            data-testid="export-csv-btn"
            disabled={filteredInvoices.length === 0}
            title={t("inv_export_csv")}
            className="flex items-center gap-2 bg-[#1E3050] text-white px-4 py-2 rounded-sm text-sm font-medium hover:bg-[#1E3050]/70 border border-[#1E3050] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            <FileSpreadsheet className="w-4 h-4" /> {t("inv_export_csv")}
          </button>
          <Dialog open={showCreate} onOpenChange={(open) => { if (!open) closeDialog(); else setShowCreate(true); }}>
            <DialogTrigger asChild>
              <button data-testid="create-invoice-btn" className="flex items-center gap-2 bg-[#F59073] text-black px-4 py-2 rounded-sm text-sm font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)] transition-all"><Plus className="w-4 h-4" /> {t("inv_new_btn")}</button>
            </DialogTrigger>
            <DialogContent className="bg-[#0F1F3A] border-[#1E3050] text-white max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-outfit">{editingId ? t("inv_edit_title", { id: editingId.slice(0, 8).toUpperCase() }) : t("inv_create_title")}</DialogTitle></DialogHeader>
              <form onSubmit={createInvoice} className="space-y-4 mt-4">
                {form.type === "one_time" && !editingId && (
                  <div className="bg-[#0A8294]/10 border border-[#0A8294]/30 rounded-sm p-3 text-xs text-[#0A8294] leading-relaxed" data-testid="invoice-type-hint">
                    <strong>Engångsfaktura</strong> — endast för uppdrag med fast pris (t.ex. hemsidebygge 2000 SEK).
                    Löpande kostnader (hosting, support, uppdateringar) hanteras via <strong>Avtal</strong> och faktureras automatiskt månads- eller årsvis.
                  </div>
                )}
                {form.type === "recurring" && !editingId && (
                  <div className="bg-[#F59073]/10 border border-[#F59073]/30 rounded-sm p-3 text-xs text-[#F59073] leading-relaxed">
                    <strong>Återkommande faktura</strong> — för hosting/support som faktureras enligt vald cykel nedan. Rekommenderat: skapa ett Avtal istället så genereras fakturor automatiskt.
                  </div>
                )}
                {formErrors.length > 0 && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-3 text-red-400 text-sm space-y-1" data-testid="form-errors">
                    {formErrors.map((e, i) => <div key={i}>• {e}</div>)}
                  </div>
                )}
                <select required disabled={!!editingId} value={form.customer_id} onChange={e => setForm(p => ({ ...p, customer_id: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none disabled:opacity-60" data-testid="create-invoice-customer">
                  <option value="">{t("inv_field_customer_placeholder")}</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
                    <option value="one_time">{t("inv_type_one_time")}</option>
                    <option value="recurring">{t("inv_type_recurring")}</option>
                  </select>
                  <select value={form.currency} onChange={e => setForm(p => ({ ...p, currency: e.target.value }))} data-testid="create-invoice-currency" className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
                    <option value="EUR">EUR</option><option value="SEK">SEK</option>
                  </select>
                  <select value={form.vat_option} onChange={e => setForm(p => ({ ...p, vat_option: e.target.value }))} data-testid="create-invoice-vat" className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
                    <option value="auto">{t("inv_vat_auto")}</option>
                    <option value="never">{t("inv_vat_never")}</option>
                    <option value="always">{t("inv_vat_always")}</option>
                    <option value="no_vat">{t("inv_vat_no_vat")}</option>
                  </select>
                  <select value={form.due_days} onChange={e => {
                    const days = Number(e.target.value);
                    const d = new Date(); d.setDate(d.getDate() + days);
                    setForm(p => ({ ...p, due_days: days, due_date: d.toISOString().slice(0, 10) }));
                  }} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" data-testid="due-days-select">
                    {[10, 14, 20, 30, 45, 60, 90].map(d => <option key={d} value={d}>{t("inv_field_due_in", { n: d })}</option>)}
                  </select>
                </div>
                {form.due_date && <p className="text-xs text-[#857973]">{t("inv_field_due")}: {form.due_date}</p>}
                {form.type === "recurring" && (
                  <select value={form.recurring_interval} onChange={e => setForm(p => ({ ...p, recurring_interval: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
                    <option value="monthly">{t("inv_field_monthly")}</option>
                    <option value="quarterly">{t("inv_field_quarterly")}</option>
                    <option value="yearly">{t("inv_field_yearly")}</option>
                  </select>
                )}

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-[10px] text-[#857973]">{t("inv_line_items")}</span>
                    {articles.length > 0 && (
                      <select data-testid="article-picker" onChange={e => { if (e.target.value) { addArticleToItems(e.target.value); e.target.value = ""; } }} className="bg-[#0A1628] border border-[#1E3050] text-[#857973] px-2 py-1 rounded-sm text-xs focus:border-[#D9A872] outline-none">
                        <option value="">{t("inv_add_from_articles")}</option>
                        {articles.map(a => {
                          const isRec = a.type === "recurring";
                          const cad = isRec ? (a.billing_interval === "yearly" ? "↻ år" : a.billing_interval === "quarterly" ? "↻ kvartal" : "↻ månad") : "engång";
                          return <option key={a.id} value={a.id}>{a.name} — {Number(a.unit_price).toLocaleString()} {a.currency || "SEK"} · {cad}</option>;
                        })}
                      </select>
                    )}
                  </div>
                  <div className="grid grid-cols-[1fr_80px_100px_30px] gap-2 mb-1">
                    <span className="text-[10px] text-[#857973] font-mono">{t("inv_col_description")}</span>
                    <span className="text-[10px] text-[#857973] font-mono">{t("inv_col_qty")}</span>
                    <span className="text-[10px] text-[#857973] font-mono">{t("inv_col_unit_price")}</span>
                    <span></span>
                  </div>
                  {items.map((item, i) => (
                    <div key={i} className="grid grid-cols-[1fr_80px_100px_30px] gap-2 mb-2">
                      <input placeholder={t("inv_col_description")} value={item.description} onChange={e => { const n = [...items]; n[i].description = e.target.value; setItems(n); }} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" data-testid={`item-desc-${i}`} />
                      <input type="number" step="0.01" placeholder={t("inv_col_qty")} value={item.quantity} onChange={e => { const n = [...items]; n[i].quantity = Number(e.target.value); setItems(n); }} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none text-center" data-testid={`item-qty-${i}`} />
                      <input type="number" step="0.01" placeholder={t("inv_col_unit_price")} value={item.unit_price} onChange={e => { const n = [...items]; n[i].unit_price = Number(e.target.value); setItems(n); }} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" data-testid={`item-price-${i}`} />
                      <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-[#857973] hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                  <button type="button" onClick={() => setItems([...items, { description: "", quantity: 1, unit_price: 0, article_id: "" }])} className="text-xs text-[#F59073] hover:underline">{t("inv_add_manual")}</button>
                </div>

                <div>
                  <span className="font-mono text-[10px] text-[#857973] block mb-2">{t("inv_discount")}</span>
                  <div className="grid grid-cols-[100px_1fr_1fr] gap-2">
                    <select value={discount.type} onChange={e => setDiscount(p => ({ ...p, type: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-2 py-2 rounded-sm text-xs focus:border-[#D9A872] outline-none">
                      <option value="percentage">{t("inv_discount_pct")}</option>
                      <option value="fixed">{t("inv_discount_fixed")}</option>
                    </select>
                    <input type="number" step="0.01" placeholder={discount.type === "percentage" ? t("inv_discount_placeholder_pct") : t("inv_discount_placeholder_amt")} value={discount.value || ""} onChange={e => setDiscount(p => ({ ...p, value: Number(e.target.value) }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" data-testid="discount-value" />
                    <input placeholder={t("inv_discount_reason")} value={discount.description} onChange={e => setDiscount(p => ({ ...p, description: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
                  </div>
                </div>

                <div className="p-3 bg-black/40 rounded-sm border border-[#1E3050] space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#857973]">{t("inv_subtotal")}</span>
                    <span className="text-neutral-300 font-mono">{subtotal.toLocaleString()} {form.currency}</span>
                  </div>
                  {discount.value > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-[#F59073]">{t("inv_discount")} {discount.type === "percentage" ? `(${discount.value}%)` : ""}{discount.description ? ` — ${discount.description}` : ""}</span>
                      <span className="text-[#F59073] font-mono">-{discountAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })} {form.currency}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-1 border-t border-[#1E3050]">
                    <span className="font-mono text-xs text-[#857973]">{t("inv_total")}</span>
                    <span className="font-mono text-lg text-white">{total.toLocaleString(undefined, { minimumFractionDigits: 2 })} {form.currency}</span>
                  </div>
                </div>

                <button type="submit" data-testid="create-invoice-submit" className="w-full bg-[#F59073] text-black py-2.5 rounded-sm text-sm font-medium">{editingId ? t("inv_save_changes") : t("inv_create_btn")}</button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search bar */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#857973]" />
        <input data-testid="invoice-search" value={search} onChange={e => setSearch(e.target.value)}
          placeholder={t("inv_search_placeholder")}
          className="w-full bg-[#0F1F3A] border border-[#1E3050] text-white pl-10 pr-4 py-2.5 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex gap-1 flex-wrap">
          {STATUSES.map(s => (
            <button key={s} onClick={() => setFilterStatus(s)} data-testid={`filter-status-${s}`} className={`px-3 py-2 rounded-sm text-xs font-mono transition-colors ${filterStatus === s ? "bg-[#F59073]/10 text-[#F59073] border border-[#F59073]/30" : "bg-[#0F1F3A] text-[#857973] border border-[#1E3050] hover:text-white"}`}>{t(`inv_status_${s}`)}</button>
          ))}
        </div>
        <div className="flex gap-1 flex-wrap">
          {TYPES.map(tp => (
            <button key={tp} onClick={() => setFilterType(tp)} data-testid={`filter-type-${tp}`} className={`px-3 py-2 rounded-sm text-xs font-mono transition-colors ${filterType === tp ? "bg-purple-500/10 text-purple-400 border border-purple-500/30" : "bg-[#0F1F3A] text-[#857973] border border-[#1E3050] hover:text-white"}`}>{t(`inv_type_${tp}`)}</button>
          ))}
        </div>
      </div>

      <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead>
            <tr className="border-b border-[#1E3050]">
              <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">{t("inv_col_invoice")}</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">{t("inv_col_customer")}</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">{t("inv_col_type")}</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">{t("inv_col_amount")}</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">{t("inv_col_status")}</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">{t("inv_col_due")}</th>
              <th className="text-right px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">{t("inv_col_actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.map(inv => (
              <tr key={inv.id} className="border-b border-[#1E3050] hover:bg-[#1E3050]/20">
                <td className="px-4 py-3">
                  <span className="text-xs text-[#857973] font-mono">{inv.id?.slice(0, 8).toUpperCase()}</span>
                  {inv.type === "credit_note" && <span className="ml-1.5 text-[10px] font-mono text-orange-400">CREDIT</span>}
                  {inv.credited && <span className="ml-1.5 text-[10px] font-mono text-orange-400/60">CREDITED</span>}
                </td>
                <td className="px-4 py-3 text-sm text-white">{custName(inv.customer_id)}</td>
                <td className="px-4 py-3 text-xs text-[#857973] font-mono">{t(`inv_type_${inv.type}`) || inv.type}</td>
                <td className="px-4 py-3">
                  <span className="text-sm text-white font-mono">{inv.total_amount?.toLocaleString()} {inv.currency}</span>
                  {inv.discount_amount > 0 && <span className="text-[10px] text-[#F59073] font-mono block">-{inv.discount_amount} rabatt</span>}
                </td>
                <td className="px-4 py-3">
                  <select value={inv.status} onChange={e => updateStatus(inv.id, e.target.value)} className={`text-xs font-mono px-2 py-1 rounded-sm border cursor-pointer focus:outline-none ${STATUS_COLORS[inv.status] || "bg-[#0A1628] border-[#1E3050] text-white"}`} data-testid={`status-${inv.id}`}>
                    {STATUSES.filter(s => s !== "all").map(s => <option key={s} value={s} className="bg-[#0A1628] text-white">{t(`inv_status_${s}`)}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 text-xs text-[#857973]">{inv.due_date || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 justify-end flex-wrap">
                    <ActionButton onClick={() => downloadPdf(inv.id)} testId={`invoice-pdf-${inv.id}`} label={t("inv_action_pdf")} icon={Download} color="hover:text-[#F59073]" />
                    {inv.status === "draft" && inv.type !== "credit_note" && (
                      <ActionButton onClick={() => openEdit(inv)} testId={`edit-invoice-${inv.id}`} label={t("inv_action_edit")} icon={Pencil} color="hover:text-[#F59073]" />
                    )}
                    {inv.status === "paid" && (
                      <>
                        <ActionButton onClick={() => downloadReceipt(inv.id)} testId={`receipt-pdf-${inv.id}`} label={t("inv_action_receipt")} icon={FileCheck} color="hover:text-green-400" />
                        <ActionButton onClick={() => openSendDialog(inv, "receipt")} testId={`send-receipt-${inv.id}`} label={t("inv_action_send_receipt")} icon={Mail} color="hover:text-green-400" />
                      </>
                    )}
                    {!inv.credited && inv.type !== "credit_note" && inv.status !== "credited" && (
                      <ActionButton onClick={() => creditInvoice(inv)} testId={`credit-${inv.id}`} label={t("inv_action_credit")} icon={RotateCcw} color="hover:text-orange-400" />
                    )}
                    {inv.status !== "paid" && inv.type !== "credit_note" && (
                      <ActionButton onClick={() => markAsPaid(inv)} testId={`mark-paid-${inv.id}`} label={t("inv_action_mark_paid")} icon={CheckCircle2} color="hover:text-green-400" />
                    )}
                    {inv.status !== "paid" && (
                      <ActionButton onClick={() => copyPayLink(inv.id)} testId={`copy-link-${inv.id}`} label={t("inv_action_copy_link")} icon={Link2} color="hover:text-green-400" />
                    )}
                    {inv.status !== "paid" && (
                      <ActionButton onClick={() => openSendDialog(inv, "invoice")} testId={`invoice-send-${inv.id}`} label={t("inv_action_send")} icon={Send} color="hover:text-[#F59073]" />
                    )}
                    {inv.status !== "paid" && (
                      <ActionButton onClick={() => openSendDialog(inv, "reminder")} testId={`invoice-reminder-${inv.id}`} label={t("inv_action_reminder")} icon={Bell} color="hover:text-yellow-400" />
                    )}
                    <ActionButton onClick={() => deleteInvoice(inv)} testId={`delete-invoice-${inv.id}`} label={t("inv_action_delete")} icon={Trash2} color="hover:text-red-400" />
                  </div>
                </td>
              </tr>
            ))}
            {filteredInvoices.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-[#857973]" data-testid="invoices-empty">{t("inv_no_results")}</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Send Email Dialog */}
      {sendDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSendDialog(null)} />
          <div className="relative bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-6 w-full max-w-md z-10">
            <div className="flex justify-between mb-5">
              <h3 className="font-outfit font-bold text-lg text-white">{sendDialogTitle}</h3>
              <button onClick={() => setSendDialog(null)} className="text-[#857973] hover:text-white">&times;</button>
            </div>

            <div className="bg-black/40 border border-[#1E3050] rounded-sm p-4 mb-5">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[#857973]">{t("inv_send_invoice_id")}</span>
                <span className="text-white font-mono">{sendDialog.id?.slice(0, 8).toUpperCase()}</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[#857973]">{t("inv_send_customer")}</span>
                <span className="text-white">{custName(sendDialog.customer_id)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#857973]">{t("inv_send_amount")}</span>
                <span className="text-white font-mono">{sendDialog.total_amount?.toLocaleString()} {sendDialog.currency}</span>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="font-mono text-[10px] text-[#857973] mb-2 block">{t("inv_send_recipient")}</label>
                <input
                  data-testid="send-email-input"
                  type="email"
                  required
                  value={sendEmail}
                  onChange={e => setSendEmail(e.target.value)}
                  className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2.5 rounded-sm text-sm focus:border-[#D9A872] outline-none"
                  placeholder="customer@company.com"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={sendPdf} onChange={e => setSendPdf(e.target.checked)} className="rounded-sm" />
                <span className="text-sm text-[#857973]">{t("inv_send_attach_pdf")}</span>
              </label>

              {sendResult && (
                <div className={`p-3 rounded-sm text-sm ${sendResult.success ? "bg-green-500/10 text-green-400 border border-green-500/20" : "bg-red-500/10 text-red-400 border border-red-500/20"}`} data-testid="send-result">
                  {sendResult.message}
                </div>
              )}

              <div className="flex gap-2">
                <button
                  data-testid="send-email-submit"
                  onClick={handleSend}
                  disabled={sending || !sendEmail}
                  className="flex-1 bg-[#F59073] text-black py-2.5 rounded-sm text-sm font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : sendType === "reminder" ? <Bell className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                  {sending ? t("inv_send_sending") : sendBtnLabel}
                </button>
                <button onClick={() => setSendDialog(null)} className="bg-[#1E3050]/40 text-white px-4 py-2.5 rounded-sm text-sm border border-[#1E3050] hover:bg-white/10">{t("common_cancel")}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ActionButton({ onClick, testId, label, icon: Icon, color }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      title={label}
      aria-label={label}
      className={`text-[#857973] transition-colors p-1.5 rounded-sm hover:bg-[#1E3050]/40 ${color}`}
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}
